import { useState, useEffect, useRef, useMemo } from 'react';
import { ArrowUp, ArrowDown, Zap, Users, Building2, Leaf, TreePine, ChevronDown, Info, Battery, Sun } from 'lucide-react';
import { Property } from '../mock-data';
import { cn } from '@/lib/utils';
import { Switch } from '@headlessui/react';
import AutoChar from '@/assets/altochar.png';
import { Tooltip } from 'react-tooltip';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { useChartEnergyData } from '@/api/hooks/useChartEnergyData';
import type { TimePeriod } from '../types';
import { TIME_PERIODS, formatPeriodDisplay, formatPeriodTitle } from '../types';

interface ConsumptionOverviewProps {
  data: Property[];
  selectedSites: string[];
  isDemoMode: boolean;
  selectedPeriod: TimePeriod;
  onPeriodChange: (period: TimePeriod) => void;
  aggregatedData?: any[];
  isLoadingAggregatedData?: boolean;
  aggregatedMetrics?: {
    totalEnergy: number;
    totalSolarEnergy: number;
    totalGridEnergy: number;
    totalOccupancy: number;
    totalInCount: number;
    totalOutCount: number;
    averageEUI: number;
    energySavingSaved: number;
    totalEnergySavingSaved: number;
    carbonEmissionSaved: number;
    equivalentTreesPlanted: number;
    avgOutdoorTemp: number;
    avgIndoorTemp: number;
    selectedSitesCount: number;
    totalArea: number;
    perSiteMetrics?: any[];
  } | null;
  dateRange?: {
    start: string;
    end: string;
  };
}

export function ConsumptionOverview({ data, selectedSites, isDemoMode, selectedPeriod, onPeriodChange, aggregatedData, isLoadingAggregatedData, aggregatedMetrics, dateRange }: ConsumptionOverviewProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSolarSavings, setShowSolarSavings] = useState(false);
  const [showStackedChart, setShowStackedChart] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Helper function to format large numbers
  const formatValue = (value: number): string => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `${Math.round(value / 1000)}K`;
    return value.toString();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredData = data.filter(site => selectedSites.includes(site.code));
  
  // Get site IDs for selected sites that are online
  const selectedSiteIds = useMemo(() => {
    return data
      .filter(site => selectedSites.includes(site.code) && site.isOnline)
      .map(site => site.id);
  }, [data, selectedSites]);
  
  // Fetch chart energy data
  const { data: chartEnergyData, isLoading: isLoadingChartData } = useChartEnergyData({
    siteIds: selectedSiteIds,
    startDate: dateRange?.start || '',
    endDate: dateRange?.end || '',
    period: selectedPeriod,
    enabled: !isDemoMode && !!dateRange && selectedSiteIds.length > 0
  });
  
  // Performance metrics - conditional based on demo mode and selected period
  const performanceMetrics = useMemo(() => {
    // Use pre-calculated metrics if available
    if (!isDemoMode && aggregatedMetrics) {
      return {
        autopilotSavings: Math.round(aggregatedMetrics.energySavingSaved), 
        solarSavings: Math.round(aggregatedMetrics.totalSolarEnergy),
        carbonSaved: Math.round(aggregatedMetrics.carbonEmissionSaved),
        electricityConsumption: Math.round(aggregatedMetrics.totalEnergy),
        accumulatedOccupancy: Math.round(aggregatedMetrics.totalOccupancy),
        treesPlanted: Math.round(aggregatedMetrics.equivalentTreesPlanted),
        gridEnergy: Math.round(aggregatedMetrics.totalGridEnergy),
        thbRate: 3.5
      };
    }

    const getBaseMetrics = () => {
      const base = {
        autopilotSavings: 21148310,
        solarSavings: 832000,
        carbonSaved: 8981852,
        electricityConsumption: 104256600,
        accumulatedOccupancy: 1542310,
        treesPlanted: 268115,
        gridEnergy: Math.floor(104256600 * 0.7), // 70% of total is grid
        thbRate: 3.5
      };

      // Adjust metrics based on selected period
      switch (selectedPeriod) {
        case 'this-month':
          return {
            ...base,
            autopilotSavings: Math.floor(base.autopilotSavings * 0.083), // ~1/12 for monthly
            solarSavings: Math.floor(base.solarSavings * 0.083),
            carbonSaved: Math.floor(base.carbonSaved * 0.083),
            electricityConsumption: Math.floor(base.electricityConsumption * 0.083),
            accumulatedOccupancy: Math.floor(base.accumulatedOccupancy * 0.083),
            treesPlanted: Math.floor(base.treesPlanted * 0.083),
        gridEnergy: Math.floor(base.electricityConsumption * 0.7 * 0.083) // 70% of total is grid
          };
        case 'this-year':
          return {
            ...base,
            gridEnergy: Math.floor(base.electricityConsumption * 0.7) // 70% of total is grid
          }; // Base values represent yearly data
        case 'all-time':
          return {
            ...base,
            autopilotSavings: Math.floor(base.autopilotSavings * 2.5), // 2.5x for all-time
            solarSavings: Math.floor(base.solarSavings * 2.5),
            carbonSaved: Math.floor(base.carbonSaved * 2.5),
            electricityConsumption: Math.floor(base.electricityConsumption * 2.5),
            accumulatedOccupancy: Math.floor(base.accumulatedOccupancy * 2.5),
            treesPlanted: Math.floor(base.treesPlanted * 2.5),
            gridEnergy: Math.floor(base.electricityConsumption * 0.7 * 2.5) // 70% of total is grid
          };
        default:
          return base;
      }
    };

    if (isDemoMode || !aggregatedData || aggregatedData.length === 0) {
      // Demo mode or no real data: use mock data adjusted by period
      return getBaseMetrics();
    } else {
      // Production mode: calculate from real aggregated data
      const selectedSiteIds = data
        .filter(site => selectedSites.includes(site.code))
        .map(site => site.id);
      
      const filteredData = aggregatedData.filter(d => 
        d && selectedSiteIds.includes(d.siteId)
      );

      // Sum up all metrics from selected sites
      const totalSolarEnergy = filteredData.reduce((sum, d) => sum + (d?.energy?.solarEnergy || 0), 0);
      const totalGridEnergy = filteredData.reduce((sum, d) => sum + (d?.energy?.gridEnergy || 0), 0);
      const totalCarbonSaved = filteredData.reduce((sum, d) => sum + (d?.energy?.carbonEmissionSaved || 0), 0);
      const totalOccupancy = filteredData.reduce((sum, d) => sum + Math.max(0, d?.occupancy?.totalOccupancy || 0), 0);
      const totalTreesPlanted = filteredData.reduce((sum, d) => sum + (d?.energy?.equivalentTreesPlanted || 0), 0);
      const totalEnergySavings = filteredData.reduce((sum, d) => sum + (d?.energy?.totalEnergySavingSaved || 0), 0);

      return {
        autopilotSavings: Math.round(totalEnergySavings * 0.8), // 80% from autopilot
        solarSavings: Math.round(totalSolarEnergy),
        carbonSaved: Math.round(totalCarbonSaved),
        electricityConsumption: Math.round(totalGridEnergy + totalSolarEnergy),
        accumulatedOccupancy: Math.round(totalOccupancy),
        treesPlanted: Math.round(totalTreesPlanted),
        gridEnergy: Math.round(totalGridEnergy),
        thbRate: 3.5
      };
    }
  }, [isDemoMode, selectedPeriod, aggregatedData, data, selectedSites, aggregatedMetrics]);

  const { autopilotSavings, solarSavings, carbonSaved, electricityConsumption, accumulatedOccupancy, treesPlanted, thbRate } = performanceMetrics;
  const totalSavings = showSolarSavings ? (autopilotSavings + solarSavings) : autopilotSavings;
  const savingsPercentage = showSolarSavings ? 19.42 : 16.86;
  const totalSavingsTHB = totalSavings * thbRate;

  // Chart data - use real data when available, otherwise use mock data
  const chartData = useMemo(() => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Use real data if available
    if (!isDemoMode && chartEnergyData && chartEnergyData.length > 0) {
      if (selectedPeriod === 'this-month') {
        // For daily data, show day number
        return chartEnergyData.map(item => {
          const day = parseInt(item.date.split('-')[2], 10);
          return {
            name: day.toString(),
            grid: item.grid,
            solar: item.solar,
            total: item.total
          };
        });
      } else {
        // For year/all-time views, ensure all 12 months are shown
        const dataMap = new Map<number, typeof chartEnergyData[0]>();
        
        // Map existing data by month
        chartEnergyData.forEach(item => {
          const monthIndex = parseInt(item.date.split('-')[1], 10) - 1;
          dataMap.set(monthIndex, item);
        });
        
        // Create array with all 12 months
        return months.map((month, index) => {
          const data = dataMap.get(index);
          return {
            name: month,
            grid: data?.grid || 0,
            solar: data?.solar || 0,
            total: data?.total || 0
          };
        });
      }
    }

    // Fall back to mock data
    if (selectedPeriod === 'this-month') {
      const baseConsumption = [
        95000, 98000, 92000, 96000, 94000, 88000, 87000,
        96000, 99000, 93000, 97000, 95000, 89000, 85000,
        94000, 97000, 91000, 95000, 93000, 90000, 88000,
        97000, 100000, 94000, 98000, 96000, 91000, 87000,
        95000, 93000
      ];
      return baseConsumption.map((consumption, i) => ({
        name: (i + 1).toString(),
        grid: Math.floor(consumption * 0.7),
        solar: Math.floor(consumption * 0.3),
        total: consumption
      }));
    } else {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const baseConsumption = [
        2400000, 2300000, 2500000, 2450000, 2600000, 2800000,
        2900000, 2850000, 2700000, 2550000, 2450000, 2500000
      ];
      return months.map((month, i) => ({
        name: month,
        grid: Math.floor(baseConsumption[i] * 0.7),
        solar: Math.floor(baseConsumption[i] * 0.3),
        total: baseConsumption[i]
      }));
    }
  }, [isDemoMode, chartEnergyData, selectedPeriod]);

  return (
    <div className="bg-white rounded-lg shadow-card border border-light-grey p-3 pt-2">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <div className="w-[20px] h-[20px] bg-primary/10 rounded flex items-center justify-center">
            <Building2 className="w-3.5 h-3.5 text-primary" />
          </div>
          <div className="flex items-center gap-2">
            <h2 className="heading-10 text-dark-blue">
              {formatPeriodTitle(selectedPeriod)} Performance
            </h2>
            <span className="text-[9px] font-medium text-dark-grey bg-light-grey px-2 py-0.5 rounded">
              {formatPeriodDisplay(selectedPeriod)}
            </span>
            <span className="text-[9px] font-medium text-dark-grey">•</span>
            <span className="text-[9px] font-medium text-dark-grey">
              {filteredData.filter(site => site.isOnline).length}/{filteredData.length} Branches
            </span>
          </div>
        </div>
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 paragraph-11 font-medium rounded-md border transition-all",
              showDropdown
                ? "text-primary border-primary bg-primary/5"
                : "text-dark-grey border-light-grey bg-white hover:border-primary hover:text-primary"
            )}
          >
            {TIME_PERIODS.find(p => p.id === selectedPeriod)?.label}
            <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", showDropdown && "rotate-180")} />
          </button>
          {showDropdown && (
            <div className="absolute right-0 mt-1 bg-white border border-light-grey rounded-md shadow-dropdown z-20 min-w-[120px] py-1">
              {TIME_PERIODS.map(period => (
                <button
                  key={period.id}
                  onClick={() => {
                    onPeriodChange(period.id);
                    setShowDropdown(false);
                  }}
                  className={cn(
                    "block w-full text-left px-3 py-2 paragraph-11 transition-colors",
                    selectedPeriod === period.id
                      ? "text-primary font-medium bg-primary/5"
                      : "text-dark-grey hover:bg-primary/5 hover:text-primary"
                  )}
                >
                  {period.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-[6px]">
        <div className={cn(
          "relative rounded-[12px] p-[6px] px-[8px] text-white overflow-hidden shadow-popup border",
          showSolarSavings
            ? "bg-gradient-to-br from-primary to-green border-green"
            : "bg-gradient-to-br from-[#0E7EE4] to-[#55A6F2] border-[#55A6F2]"
        )}>
          <div className="w-full flex justify-between items-center mb-2">
            <div className="gap-[6px] flex justify-start items-center">
              <div className={cn(
                "w-[24px] h-[24px] rounded-[20px] pt-[4px] pl-[1px] overflow-hidden",
                "bg-[#3E7FC7]"
              )}>
                <img
                  src={AutoChar}
                  alt="Autopilot"
                  className="w-[22px] h-[22px] object-cover"
                />
              </div>
              <p className="heading-10 text-white">Saving</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="paragraph-9 font-medium opacity-90">
                {showSolarSavings ? 'Autopilot + Solar' : 'Autopilot Only'}
              </span>
              <Switch
                checked={showSolarSavings}
                onChange={setShowSolarSavings}
                className={cn(
                  "relative inline-flex h-4 w-8 items-center rounded-full transition-colors",
                  "bg-[#3E7FC7]"
                )}
              >
                <span className="sr-only">Toggle solar savings</span>
                <span
                  className={cn(
                    "inline-block h-3 w-3 transform rounded-full bg-white transition-transform",
                    showSolarSavings ? "translate-x-4" : "translate-x-1"
                  )}
                />
              </Switch>
            </div>
          </div>

          <div className="relative z-10 mt-1">
            <div className="space-y-3">
              <div className="flex items-baseline gap-[6px]">
                {isLoadingAggregatedData && !isDemoMode ? (
                  <div className="h-8 w-32 bg-white/20 animate-pulse rounded"></div>
                ) : (
                  <span className="heading-2 leading-none tracking-tight">{totalSavingsTHB.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                )}
                <span className="paragraph-12 opacity-90">THB Saved</span>
              </div>
              <div className="flex items-baseline gap-[4px]">
                {isLoadingAggregatedData && !isDemoMode ? (
                  <div className="h-4 w-24 bg-white/20 animate-pulse rounded"></div>
                ) : (
                  <span className="paragraph-12 leading-none opacity-80">{totalSavings.toLocaleString()}</span>
                )}
                <span className="paragraph-9 opacity-80">kWh Saved</span>
              </div>
            </div>
          </div>

          <div
            data-font="barlow-condensed"
            className="text-[rgba(255,255,255,0.1)] text-[70px] absolute right-[5px] top-[30px] select-none pointer-events-none z-0 leading-[0.9]"
            style={{
              letterSpacing: '-0.02em',
              fontStretch: 'condensed',
              fontWeight: 600
            }}
          >
            {savingsPercentage.toFixed(1)}<span style={{ fontSize: '60%', verticalAlign: 'top', marginLeft: '-1px' }}>%</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-3">
          <div className="rounded-md p-2 border border-light-grey">
            <div className="flex items-center gap-1 mb-1">
              <div className="w-[16px] h-[16px] bg-primary/10 rounded flex items-center justify-center">
                <Zap className="w-2.5 h-2.5 text-primary" />
              </div>
              <span className="heading-8 text-dark-blue">Energy Consumption</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-baseline gap-1">
                {isLoadingAggregatedData && !isDemoMode ? (
                  <div className="h-5 w-16 bg-gray-200 animate-pulse rounded"></div>
                ) : (
                  <>
                    <span className="heading-5 text-dark">
                      {formatValue(electricityConsumption)}
                    </span>
                    <span className="paragraph-10 text-dark-grey">kWh</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-0.5">
                <ArrowUp className="w-3 h-3 text-danger" />
                <span className="paragraph-10 font-medium text-danger">+5.18%</span>
              </div>
            </div>
          </div>

          <div className="rounded-md p-2 border border-light-grey">
            <div className="flex items-center gap-1 mb-1">
              <div className="w-[16px] h-[16px] bg-primary/10 rounded flex items-center justify-center">
                <Battery className="w-2.5 h-2.5 text-primary" />
              </div>
              <span className="heading-8 text-dark-blue">Grid Import</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-baseline gap-1">
                <span className="heading-5 text-dark">
                  {formatValue(performanceMetrics.gridEnergy || 0)}
                </span>
                <span className="paragraph-10 text-dark-grey">kWh</span>
              </div>
              <div className="flex items-center gap-0.5">
                <ArrowDown className="w-3 h-3 text-green" />
                <span className="paragraph-10 font-medium text-green">+3.31%</span>
              </div>
            </div>
          </div>

          <div className="rounded-md p-2 border border-light-grey">
            <div className="flex items-center gap-1 mb-1">
              <div className="w-[16px] h-[16px] bg-primary/10 rounded flex items-center justify-center">
                <Sun className="w-2.5 h-2.5 text-primary" />
              </div>
              <span className="heading-8 text-dark-blue">Solar Generation</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-baseline gap-1">
                <span className="heading-5 text-dark">
                  {formatValue(solarSavings)}
                </span>
                <span className="paragraph-10 text-dark-grey">kWh</span>
              </div>
              <div className="flex items-center gap-0.5">
                <ArrowUp className="w-3 h-3 text-green" />
                <span className="paragraph-10 font-medium text-green">+2.74%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-[6px] p-[4px] border border-light-grey">
            <div className="flex items-center gap-1 mb-1">
              <div className="w-[14px] h-[14px] bg-primary/5 rounded flex items-center justify-center">
                <Users className="w-2 h-2 text-primary/70" />
              </div>
              <span className="paragraph-8 font-medium text-dark-grey">Accumulate Occupancy</span>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="paragraph-14 font-medium text-dark-grey">{accumulatedOccupancy.toLocaleString()}</span>
              <span className="paragraph-8 text-dark-grey">ppl</span>
            </div>
          </div>

          <div className="rounded-[6px] p-[4px] border border-light-grey">
            <div className="flex items-center gap-1 mb-1">
              <div className="w-[14px] h-[14px] bg-primary/5 rounded flex items-center justify-center">
                <Leaf className="w-2 h-2 text-primary/70" />
              </div>
              <span className="paragraph-8 font-medium text-dark-grey">Carbon Emission Saved</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="flex items-baseline gap-1">
                <span className="paragraph-14 font-medium text-dark-grey">{carbonSaved.toLocaleString()}</span>
                <span className="paragraph-8 text-dark-grey">kg-CO2</span>
              </div>
              <div
                data-tooltip-id="carbon-emission-tooltip"
                data-tooltip-place="top"
                className="cursor-pointer text-gray-400 hover:text-gray-600 flex"
              >
                <Info className="inline-block h-[8px] w-[8px] text-primary/60" />
              </div>
            </div>
          </div>

          <div className="rounded-[6px] p-[4px] border border-light-grey">
            <div className="flex items-center gap-1 mb-1">
              <div className="w-[14px] h-[14px] bg-primary/5 rounded flex items-center justify-center">
                <TreePine className="w-2 h-2 text-primary/70" />
              </div>
              <span className="paragraph-8 font-medium text-dark-grey">Equivalent Trees Planted</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="flex items-baseline gap-1">
                <span className="paragraph-14 font-medium text-dark-grey">{treesPlanted.toLocaleString()}</span>
                <span className="paragraph-8 text-dark-grey">trees</span>
              </div>
              <div
                data-tooltip-id="trees-planted-tooltip"
                data-tooltip-place="top"
                className="cursor-pointer text-gray-400 hover:text-gray-600 flex"
              >
                <Info className="inline-block h-[8px] w-[8px] text-primary/60" />
              </div>
            </div>
          </div>
        </div>

        {/* Bar Chart Section */}
        <div className="mt-3 border border-light-grey rounded-lg p-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h3 className="text-[11px] font-semibold text-dark-blue">
                {showStackedChart ? 'Grid Import & Solar Generation (kWh)' : 'Energy Consumption (kWh)'} by {selectedPeriod === 'this-month' ? 'Day' : 'Month'}
              </h3>
              <span className="text-[9px] font-medium text-dark-grey bg-light-grey px-2 py-0.5 rounded">
                {formatPeriodDisplay(selectedPeriod)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {showStackedChart && (
                <div className="flex items-center gap-2 mr-2">
                  <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 bg-[#7D3BBF] rounded-sm"></div>
                    <span className="paragraph-9 text-dark-grey">Grid</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 bg-[#F2AA32] rounded-sm"></div>
                    <span className="paragraph-9 text-dark-grey">Solar</span>
                  </div>
                </div>
              )}
              <Switch
                checked={showStackedChart}
                onChange={setShowStackedChart}
                className="relative inline-flex h-3.5 w-7 items-center rounded-full bg-light-grey transition-colors"
              >
                <span className="sr-only">Toggle stacked view</span>
                <span
                  className={cn(
                    "inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform",
                    showStackedChart ? "translate-x-3.5" : "translate-x-0.5"
                  )}
                />
              </Switch>
              <span className="paragraph-9 text-dark-grey ml-1">Grid + Solar</span>
            </div>
          </div>

          {/* Energy Consumption Bar Chart */}
          {isLoadingChartData && !isDemoMode ? (
            <div className="h-[180px] flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={chartData} margin={{ top: 5, right: 5, left: -30, bottom: -15 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F5F5F5" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 7, fill: '#9CA3AF' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 8, fill: '#9CA3AF' }}
                axisLine={false}
                tickLine={false}
                domain={[(dataMin: number) => {
                  // If all values are 0, start from 0
                  if (dataMin === 0 && chartData.every(d => d.total === 0)) {
                    return 0;
                  }
                  // Calculate a nice minimum value (about 10% below the minimum)
                  const buffer = dataMin * 0.05;
                  return Math.floor((dataMin - buffer) / 1000) * 1000;
                }, (dataMax: number) => {
                  // If all values are 0, show a reasonable max
                  if (dataMax === 0) {
                    return 100000; // 100k default max for empty data
                  }
                  // Calculate a nice maximum value (about 10% above the maximum)
                  const buffer = dataMax * 0.025;
                  return Math.ceil((dataMax + buffer) / 1000) * 1000;
                }]}
                tickFormatter={(value) => {
                  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                  if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
                  return value;
                }}
              />
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '4px',
                  fontSize: '10px',
                  padding: '4px 8px'
                }}
                formatter={(value: number) => value === 0 ? 'No data' : `${value.toLocaleString()} kWh`}
                labelFormatter={(label) => {
                  if (selectedPeriod === 'this-month') {
                    return `Day ${label}`;
                  }
                  return label;
                }}
              />
              {showStackedChart ? (
                <>
                  <Bar dataKey="grid" stackId="a" fill="#7D3BBF" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="solar" stackId="a" fill="#F2AA32" radius={[2, 2, 0, 0]} />
                </>
              ) : (
                <Bar dataKey="total" fill="#0E7EE4" radius={[2, 2, 0, 0]} />
              )}
            </BarChart>
          </ResponsiveContainer>
          )}
        </div>
      </div>

      <Tooltip
        arrowColor='bg-white'
        id="carbon-emission-tooltip"
        opacity={1}
        style={{
          backgroundColor: 'white',
          padding: '4px 6px',
          gap: '8px',
          width: "165px",
          boxShadow: "-4px -4px 20px 0px #7090B03D",
          backdropFilter: "blur(20px)",
        }}
      >
        <p className='paragraph-9 text-dark-blue'>0.4999 kgCO2e per kWh</p>
      </Tooltip>

      <Tooltip
        arrowColor='bg-white'
        id="trees-planted-tooltip"
        opacity={1}
        style={{
          backgroundColor: 'white',
          padding: '4px 6px',
          gap: '8px',
          width: "140px",
          boxShadow: "-4px -4px 20px 0px #7090B03D",
          backdropFilter: "blur(20px)",
        }}
      >
        <p className='paragraph-9 text-dark-blue'>33.5 kg per tree/year</p>
      </Tooltip>
    </div>
  );
}